import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { useAuthStore } from '../stores/auth';
import { useAnonymousSessionStore } from '../stores/anonymousSession';

/**
 * Single axios instance for talking to core. The dev Vite proxy forwards
 * `/v1/*` to localhost:3001, so callers always hit a relative URL — that
 * way the same code works in dev and behind a reverse proxy in prod.
 *
 * Auth resolution happens in a request interceptor, not at construction
 * time, so token rotation and dev-shim toggling take effect immediately
 * without rebuilding the client.
 */
export const api = axios.create({
  baseURL: '/',
  timeout: 15_000,
});

api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const auth = useAuthStore();
  const anon = useAnonymousSessionStore();
  cfg.headers = cfg.headers ?? {};

  // Per ADR-0073. The engagement token is universally accepted by the
  // backend's tenant-context hook (it resolves to the placeholder User
  // in the pending tenant, exactly like a JWT resolves to a real user).
  //
  // Precedence is `anon.isActive` first, not JWT first: when a
  // previously-signed-in visitor lands on the anon-marketing URL and
  // picks "continue as anonymous test" in the OnboardingEntryPage
  // modal, both tokens live in stores at the same time — the pending
  // tenant is what they want every call (Copilot, SSE, scenarios) to
  // operate on, NOT their real tenant. After claim, `anon.clear()`
  // drops the engagement token and the JWT branch takes over.
  if (anon.engagementToken) {
    cfg.headers.Authorization = `Bearer ${anon.engagementToken}`;
  } else if (auth.accessToken) {
    cfg.headers.Authorization = `Bearer ${auth.accessToken}`;
  } else if (auth.devShim) {
    cfg.headers['x-tenant-id'] = auth.devShim.tenantId;
    cfg.headers['x-user-id'] = auth.devShim.userId;
  }
  return cfg;
});

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    displayName: string | null;
    tenantId: string;
    platformAdmin: boolean;
  };
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

let refreshInFlight: Promise<string | undefined> | undefined;

async function tryRefresh(): Promise<string | undefined> {
  const auth = useAuthStore();
  if (!auth.refreshToken) return undefined;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await axios.post<RefreshResponse>('/v1/auth/refresh', {
        refreshToken: auth.refreshToken,
      });
      auth.setFromSession(res.data);
      return res.data.accessToken;
    } catch {
      auth.clear();
      return undefined;
    } finally {
      refreshInFlight = undefined;
    }
  })();
  return refreshInFlight;
}

api.interceptors.response.use(
  (res: AxiosResponse) => res,
  async (err: unknown) => {
    if (!axios.isAxiosError(err) || err.response?.status !== 401) {
      return Promise.reject(err);
    }
    const original = err.config as RetriableConfig | undefined;
    const auth = useAuthStore();
    const anon = useAnonymousSessionStore();

    // Anonymous engagement session: a 401 means the token is expired /
    // wrong-scope / tenant purged. Clear the anon store so subsequent
    // requests do not keep replaying a dead token, and NEVER wipe a
    // real JWT sitting alongside (signed-in visitor who chose "continue
    // as anonymous test"). Do not JWT-refresh either — that would put a
    // real access token on the wire only for the request interceptor to
    // overwrite it with the (still-present) engagement token.
    if (anon.engagementToken) {
      anon.clear();
      return Promise.reject(err);
    }

    // Don't recurse on the refresh endpoint itself.
    if (!original || original._retried || original.url?.includes('/v1/auth/')) {
      auth.clear();
      return Promise.reject(err);
    }
    if (!auth.refreshToken) {
      auth.clear();
      return Promise.reject(err);
    }

    const fresh = await tryRefresh();
    if (!fresh) return Promise.reject(err);

    original._retried = true;
    original.headers = original.headers ?? {};
    original.headers.Authorization = `Bearer ${fresh}`;
    return api.request(original);
  },
);

export async function logout(): Promise<void> {
  const auth = useAuthStore();
  const token = auth.refreshToken;
  if (token) {
    try {
      await axios.post('/v1/auth/logout', { refreshToken: token });
    } catch {
      // Ignored — logout is best-effort. We always clear local state.
    }
  }
  auth.clear();
}

export type { AxiosError };
